import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Stack, Group, Text, Button, SimpleGrid, Skeleton, EmptyStateBox, Box, Carousel, carouselDashboard } from 'components'
import { IconClipboardText, IconPlus, IconBooks, IconFolderOff } from '@tabler/icons-react'
import { fetchMyProjects } from 'pages/ArcProjectsList/services/arcProjectsApi'
import type { ArcProject } from 'pages/ArcProjectsList/types'
import { ArcProjectCard } from './ArcProjectCard'
import { useArcDiskDemoEnabled, withDemoProjects } from 'arcdisk'

const CAROUSEL_THRESHOLD = 3
const CARD_HEIGHT = 380

export const ArcProjectsSection = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [projects, setProjects] = useState<ArcProject[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const arcDiskDemo = useArcDiskDemoEnabled()

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const result = await fetchMyProjects({ status: 'assigned' })
        // ArcDisk demo: the three demo projects join the dashboard's assigned list
        setProjects(
          arcDiskDemo
            ? withDemoProjects(result.projects, { tab: 'my', page: 1, search: '', status: 'assigned' })
            : result.projects,
        )
      } catch (error) {
        console.error('Failed to load projects:', error)
      } finally {
        setIsLoading(false)
      }
    }
    loadProjects()
  }, [arcDiskDemo])

  const useCarousel = projects.length > CAROUSEL_THRESHOLD

  const renderProjects = () => {
    if (isLoading) {
      return (
        <SimpleGrid
          cols={3}
          spacing='md'
          breakpoints={[{ maxWidth: 'md', cols: 2 }, { maxWidth: 'sm', cols: 1 }]}
        >
          {[1, 2, 3].map(i => (
            <Skeleton key={i} height={CARD_HEIGHT} radius='md' animate />
          ))}
        </SimpleGrid>
      )
    }

    if (projects.length === 0) {
      return <EmptyStateBox withBorder text={t('arc_dashboard.no_assigned_projects')} icon={<IconFolderOff />} />
    }

    if (useCarousel) {
      return (
        <Carousel {...carouselDashboard}>
          {projects.map(project => (
            <Carousel.Slide key={project.id}>
              {/* minHeight (not a fixed height) lets the card grow when text
                  wraps at narrow widths; height 100% keeps slides equalized */}
              <Box sx={{ minHeight: CARD_HEIGHT, height: '100%' }}>
                <ArcProjectCard project={project} />
              </Box>
            </Carousel.Slide>
          ))}
        </Carousel>
      )
    }

    return (
      <SimpleGrid
        cols={3}
        spacing='md'
        breakpoints={[{ maxWidth: 'md', cols: 2 }, { maxWidth: 'sm', cols: 1 }]}
      >
        {projects.map(project => (
          <Box key={project.id} sx={{ minHeight: CARD_HEIGHT }}>
            <ArcProjectCard project={project} />
          </Box>
        ))}
      </SimpleGrid>
    )
  }

  return (
    <Stack spacing='md'>
      {/* Header */}
      <Group position='apart'>
        <Group spacing='xs'>
          <IconClipboardText size={24} />
          <Text size='lg' weight={700}>
            {t('arc_projects.dashboard_title')}
          </Text>
        </Group>

        <Group spacing='xs'>
          <Button
            variant='filled'
            color='teal'
            radius='xl'
            leftIcon={<IconBooks size={18} />}
            onClick={() => navigate('/arc_projects?tab=library')}
          >
            {t('arc_dashboard.browse_library')}
          </Button>
          <Button
            variant='filled'
            color='teal'
            radius='xl'
            leftIcon={<IconPlus size={18} />}
            onClick={() => navigate('/projects/new')}
          >
            {t('arc_projects.new_project')}
          </Button>
        </Group>
      </Group>

      {/* Projects */}
      {renderProjects()}
    </Stack>
  )
}
